import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '@/components/Layout';
import {
  ArrowLeft, Folder, FileText, MessageSquare, FileSignature,
  CheckSquare, Edit2, Users, Calendar, Tag, ExternalLink,
  AlertCircle, ChevronRight, Clock, Plus, Trash2
} from 'lucide-react';
import { useTimer } from '@/contexts/TimerContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  ouvert:   { label: 'Ouvert',   color: 'bg-emerald-100 text-emerald-700', dot: 'bg-emerald-500' },
  en_cours: { label: 'En cours', color: 'bg-blue-100 text-blue-700',      dot: 'bg-blue-500' },
  cloture:  { label: 'Clôturé',  color: 'bg-gray-100 text-gray-600',      dot: 'bg-gray-400' },
  archive:  { label: 'Archivé',  color: 'bg-gray-100 text-gray-400',      dot: 'bg-gray-300' },
};

const TABS = [
  { id: 'overview',       label: 'Vue d\'ensemble', icon: Folder },
  { id: 'documents',      label: 'Documents',       icon: FileText },
  { id: 'conversations',  label: 'Conversations IA',icon: MessageSquare },
  { id: 'contracts',      label: 'Contrats',        icon: FileSignature },
  { id: 'todos',          label: 'Tâches',          icon: CheckSquare },
  { id: 'temps',          label: 'Temps',            icon: Clock },
];

const EmptyTab = ({ icon: Icon, title, description, cta, onCta }) => (
  <div className="text-center py-16">
    <div className="w-12 h-12 bg-gray-50 rounded-xl flex items-center justify-center mx-auto mb-3">
      <Icon className="h-6 w-6 text-gray-300" />
    </div>
    <p className="font-medium text-gray-700 mb-1">{title}</p>
    <p className="text-sm text-gray-400 mb-4 max-w-xs mx-auto">{description}</p>
    {cta && (
      <button
        onClick={onCta}
        className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors"
      >
        {cta}
      </button>
    )}
  </div>
);

const DossierDetail = ({ user, cabinet, onLogout }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [dossier, setDossier] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [tabData, setTabData] = useState({});
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});

  const { timerState, start: startTimer } = useTimer();
  const isTimerRunning = timerState?.dossier_id === id;
  const [timeEntries, setTimeEntries] = useState([]);
  const [timeLoading, setTimeLoading] = useState(false);
  const [showAddTime, setShowAddTime] = useState(false);
  const [timeForm, setTimeForm] = useState({ description: '', date: new Date().toISOString().slice(0, 10), duration_minutes: 60 });
  const [dossierRate, setDossierRate] = useState(null);

  const getHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('lawliet_token')}`,
  });

  const fetchDossier = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/dossiers/${id}`, {
        headers: getHeaders(),
      });
      setDossier(data);
      setEditForm(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchTabData = async (tab) => {
    if (tabData[tab]) return;
    const endpoints = {
      documents:     `dossiers/${id}/documents`,
      conversations: `dossiers/${id}/conversations`,
      contracts:     `dossiers/${id}/contracts`,
      todos:         `dossiers/${id}/todos`,
    };
    if (!endpoints[tab]) return;
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/${endpoints[tab]}`, {
        headers: getHeaders(),
      });
      setTabData((prev) => ({ ...prev, [tab]: data }));
    } catch (err) {
      console.error(err);
    }
  };

  const loadTimeEntries = async () => {
    setTimeLoading(true);
    try {
      const { data } = await axios.get(
        `${BACKEND_URL}/api/time-entries?dossier_id=${id}`,
        { headers: getHeaders() }
      );
      setTimeEntries(data.entries || []);
    } catch (e) {
      console.error(e);
    } finally {
      setTimeLoading(false);
    }
  };

  const handleSaveTimeEntry = async () => {
    try {
      await axios.post(
        `${BACKEND_URL}/api/time-entries`,
        { ...timeForm, dossier_id: id, duration_minutes: Number(timeForm.duration_minutes) },
        { headers: getHeaders() }
      );
      toast.success('Saisie enregistrée');
      setShowAddTime(false);
      setTimeForm({ description: '', date: new Date().toISOString().slice(0, 10), duration_minutes: 60 });
      loadTimeEntries();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  const handleDeleteTimeEntry = async (entryId) => {
    if (!window.confirm('Supprimer cette saisie ?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/time-entries/${entryId}`, { headers: getHeaders() });
      toast.success('Saisie supprimée');
      loadTimeEntries();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Impossible de supprimer une saisie facturée');
    }
  };

  const handleSaveDossierRate = async (rate) => {
    try {
      await axios.patch(
        `${BACKEND_URL}/api/dossiers/${id}/hourly-rate`,
        { hourly_rate_euros: rate === '' ? null : Number(rate) },
        { headers: getHeaders() }
      );
      setDossierRate(rate);
      toast.success('Taux mis à jour');
    } catch (e) {
      toast.error('Erreur lors de la mise à jour du taux');
    }
  };

  useEffect(() => { fetchDossier(); }, [id]);
  useEffect(() => {
    if (activeTab === 'temps') {
      loadTimeEntries();
      setDossierRate(dossier?.hourly_rate_euros ?? '');
    } else if (activeTab !== 'overview') {
      fetchTabData(activeTab);
    }
  }, [activeTab]); // eslint-disable-line

  const handleSave = async () => {
    try {
      const { data } = await axios.put(
        `${BACKEND_URL}/api/dossiers/${id}`,
        editForm,
        { headers: getHeaders() }
      );
      setDossier(data);
      setEditing(false);
    } catch (err) {
      console.error(err);
    }
  };

  const handleStatusChange = async (newStatus) => {
    await axios.put(
      `${BACKEND_URL}/api/dossiers/${id}`,
      { status: newStatus },
      { headers: getHeaders() }
    );
    setDossier((d) => ({ ...d, status: newStatus }));
  };

  if (loading) {
    return (
      <Layout user={user} cabinet={cabinet} onLogout={onLogout}>
        <div className="flex items-center justify-center min-h-96">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        </div>
      </Layout>
    );
  }

  if (!dossier) {
    return (
      <Layout user={user} cabinet={cabinet} onLogout={onLogout}>
        <div className="text-center py-20">
          <AlertCircle className="h-12 w-12 text-red-400 mx-auto mb-3" />
          <p className="font-medium text-gray-800">Dossier introuvable</p>
          <button onClick={() => navigate('/dossiers')} className="mt-4 text-blue-600 text-sm">
            ← Retour aux dossiers
          </button>
        </div>
      </Layout>
    );
  }

  const status = STATUS_CONFIG[dossier.status] || STATUS_CONFIG.ouvert;

  const OverviewTab = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {/* Stats */}
      <div className="grid grid-cols-2 gap-4">
        {[
          { icon: FileText,      label: 'Documents',       value: dossier.document_count,     href: 'documents' },
          { icon: MessageSquare, label: 'Conversations IA', value: dossier.conversation_count, href: 'conversations' },
          { icon: FileSignature, label: 'Contrats',         value: dossier.contract_count,     href: 'contracts' },
          { icon: CheckSquare,   label: 'Tâches',           value: dossier.todo_count,         href: 'todos' },
        ].map((stat) => (
          <button
            key={stat.label}
            onClick={() => setActiveTab(stat.href)}
            className="bg-gray-50 hover:bg-blue-50 rounded-xl p-4 text-left transition-colors group"
          >
            <stat.icon className="h-5 w-5 text-gray-400 group-hover:text-blue-500 mb-2 transition-colors" />
            <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
            <p className="text-sm text-gray-500">{stat.label}</p>
          </button>
        ))}
      </div>

      {/* Details */}
      <div className="space-y-4">
        <div className="bg-gray-50 rounded-xl p-4 space-y-3">
          {dossier.client_name && (
            <div className="flex items-center gap-2 text-sm">
              <Users className="h-4 w-4 text-gray-400 flex-shrink-0" />
              <span className="text-gray-600">Client :</span>
              <span className="font-medium text-gray-900">{dossier.client_name}</span>
            </div>
          )}
          <div className="flex items-center gap-2 text-sm">
            <Calendar className="h-4 w-4 text-gray-400 flex-shrink-0" />
            <span className="text-gray-600">Ouverture :</span>
            <span className="font-medium text-gray-900">
              {new Date(dossier.date_ouverture).toLocaleDateString('fr-FR', {
                day: 'numeric', month: 'long', year: 'numeric',
              })}
            </span>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <Tag className="h-4 w-4 text-gray-400 flex-shrink-0" />
            <span className="text-gray-600">Type :</span>
            <span className="font-medium text-gray-900 capitalize">{dossier.type}</span>
          </div>
        </div>

        {dossier.description && (
          <div className="bg-gray-50 rounded-xl p-4">
            <p className="text-sm font-medium text-gray-700 mb-1">Description</p>
            <p className="text-sm text-gray-600">{dossier.description}</p>
          </div>
        )}

        {/* Quick actions */}
        <div className="space-y-2">
          <p className="text-sm font-medium text-gray-700">Actions rapides</p>
          {[
            { label: 'Nouvelle conversation IA sur ce dossier', href: `/chat?dossier_id=${id}`, icon: MessageSquare },
            { label: 'Importer un document', href: `/documents?dossier_id=${id}`, icon: FileText },
            { label: 'Rédiger un contrat', href: `/contracts?dossier_id=${id}`, icon: FileSignature },
          ].map((action) => (
            <button
              key={action.label}
              onClick={() => navigate(action.href)}
              className="w-full flex items-center gap-3 px-4 py-3 border border-gray-200 rounded-xl text-sm hover:border-blue-300 hover:bg-blue-50 transition-all group"
            >
              <action.icon className="h-4 w-4 text-gray-400 group-hover:text-blue-500" />
              <span className="text-gray-700 group-hover:text-blue-700">{action.label}</span>
              <ChevronRight className="h-4 w-4 text-gray-300 ml-auto group-hover:text-blue-400" />
            </button>
          ))}
        </div>
      </div>
    </div>
  );

  const ItemList = ({ items, emptyProps, renderItem }) => {
    if (!items) {
      return (
        <div className="flex items-center justify-center py-10">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" />
        </div>
      );
    }
    if (items.length === 0) return <EmptyTab {...emptyProps} />;
    return <div className="space-y-3">{items.map(renderItem)}</div>;
  };

  return (
    <Layout user={user} cabinet={cabinet} onLogout={onLogout}>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        {/* Back + Header */}
        <button
          onClick={() => navigate('/dossiers')}
          className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-800 mb-6 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" /> Tous les dossiers
        </button>

        <div className="flex items-start justify-between mb-6 flex-wrap gap-4">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
              <Folder className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              {editing ? (
                <input
                  value={editForm.title}
                  onChange={(e) => setEditForm((f) => ({ ...f, title: e.target.value }))}
                  className="text-2xl font-semibold border-b-2 border-blue-400 bg-transparent outline-none text-gray-900 w-full"
                />
              ) : (
                <h1 className="font-serif text-3xl font-semibold tracking-tight text-gray-900">{dossier.title}</h1>
              )}
              {dossier.reference && (
                <p className="text-sm text-gray-400 mt-0.5">{dossier.reference}</p>
              )}
            </div>
          </div>

          <div className="flex items-center gap-3">
            <select
              value={dossier.status}
              onChange={(e) => handleStatusChange(e.target.value)}
              className={`text-sm px-3 py-1.5 rounded-full font-medium border-0 cursor-pointer ${status.color}`}
            >
              <option value="ouvert">Ouvert</option>
              <option value="en_cours">En cours</option>
              <option value="cloture">Clôturé</option>
              <option value="archive">Archivé</option>
            </select>
            {editing ? (
              <div className="flex gap-2">
                <button
                  onClick={() => setEditing(false)}
                  className="px-3 py-1.5 border border-gray-200 text-gray-600 rounded-lg text-sm"
                >
                  Annuler
                </button>
                <button
                  onClick={handleSave}
                  className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm font-medium"
                >
                  Sauvegarder
                </button>
              </div>
            ) : (
              <button
                onClick={() => setEditing(true)}
                className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50 text-gray-500 transition-colors"
              >
                <Edit2 className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 mb-6 overflow-x-auto">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                activeTab === tab.id
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-800'
              }`}
            >
              <tab.icon className="h-4 w-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div>
          {activeTab === 'overview' && <OverviewTab />}

          {activeTab === 'documents' && (
            <ItemList
              items={tabData.documents}
              emptyProps={{
                icon: FileText,
                title: 'Aucun document dans ce dossier',
                description: 'Importez des documents depuis la section Documents et associez-les à ce dossier.',
                cta: 'Importer un document',
                onCta: () => navigate(`/documents`),
              }}
              renderItem={(doc) => (
                <div
                  key={doc.id}
                  onClick={() => navigate(`/documents`)}
                  className="flex items-center gap-4 p-4 border border-gray-200 rounded-xl hover:border-blue-300 cursor-pointer transition-all"
                >
                  <FileText className="h-5 w-5 text-blue-500 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 text-sm truncate">{doc.filename}</p>
                    <p className="text-xs text-gray-400">
                      {new Date(doc.uploaded_at).toLocaleDateString('fr-FR')}
                    </p>
                  </div>
                  <ExternalLink className="h-4 w-4 text-gray-300" />
                </div>
              )}
            />
          )}

          {activeTab === 'conversations' && (
            <ItemList
              items={tabData.conversations}
              emptyProps={{
                icon: MessageSquare,
                title: 'Aucune conversation dans ce dossier',
                description: 'Démarrez une conversation IA en lien avec ce dossier.',
                cta: 'Nouvelle conversation IA',
                onCta: () => navigate(`/chat`),
              }}
              renderItem={(conv) => (
                <div
                  key={conv.id}
                  onClick={() => navigate(`/chat/${conv.id}`)}
                  className="flex items-center gap-4 p-4 border border-gray-200 rounded-xl hover:border-blue-300 cursor-pointer transition-all"
                >
                  <MessageSquare className="h-5 w-5 text-violet-500 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 text-sm truncate">
                      {conv.title || 'Conversation sans titre'}
                    </p>
                    <p className="text-xs text-gray-400">
                      {new Date(conv.created_at).toLocaleDateString('fr-FR')}
                    </p>
                  </div>
                  <ExternalLink className="h-4 w-4 text-gray-300" />
                </div>
              )}
            />
          )}

          {activeTab === 'contracts' && (
            <ItemList
              items={tabData.contracts}
              emptyProps={{
                icon: FileSignature,
                title: 'Aucun contrat dans ce dossier',
                description: 'Rédigez un contrat et associez-le à ce dossier.',
                cta: 'Rédiger un contrat',
                onCta: () => navigate(`/contracts`),
              }}
              renderItem={(contract) => (
                <div
                  key={contract.id}
                  onClick={() => navigate(`/contracts`)}
                  className="flex items-center gap-4 p-4 border border-gray-200 rounded-xl hover:border-blue-300 cursor-pointer transition-all"
                >
                  <FileSignature className="h-5 w-5 text-emerald-500 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 text-sm truncate">{contract.title}</p>
                    <p className="text-xs text-gray-400">
                      {new Date(contract.created_at).toLocaleDateString('fr-FR')}
                    </p>
                  </div>
                  <ExternalLink className="h-4 w-4 text-gray-300" />
                </div>
              )}
            />
          )}

          {activeTab === 'todos' && (
            <ItemList
              items={tabData.todos}
              emptyProps={{
                icon: CheckSquare,
                title: 'Aucune tâche dans ce dossier',
                description: 'Créez des tâches liées à ce dossier depuis la section Tâches.',
                cta: 'Gérer les tâches',
                onCta: () => navigate(`/todos`),
              }}
              renderItem={(todo) => (
                <div
                  key={todo.id}
                  className="flex items-center gap-4 p-4 border border-gray-200 rounded-xl"
                >
                  <div
                    className={`w-4 h-4 rounded-full border-2 flex-shrink-0 ${
                      todo.status === 'done'
                        ? 'bg-emerald-500 border-emerald-500'
                        : 'border-gray-300'
                    }`}
                  />
                  <div className="flex-1 min-w-0">
                    <p
                      className={`font-medium text-sm ${
                        todo.status === 'done'
                          ? 'text-gray-400 line-through'
                          : 'text-gray-900'
                      }`}
                    >
                      {todo.title}
                    </p>
                    {todo.due_date && (
                      <p className="text-xs text-gray-400">
                        Échéance : {new Date(todo.due_date).toLocaleDateString('fr-FR')}
                      </p>
                    )}
                  </div>
                  <span
                    className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                      todo.priority === 'urgent'
                        ? 'bg-red-100 text-red-600'
                        : todo.priority === 'high'
                        ? 'bg-orange-100 text-orange-600'
                        : 'bg-gray-100 text-gray-500'
                    }`}
                  >
                    {todo.priority}
                  </span>
                </div>
              )}
            />
          )}

          {activeTab === 'temps' && (
            <div className="space-y-4">
              {/* Header */}
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-medium text-slate-700">Saisies de temps</h3>
                  {timeEntries.length > 0 && (
                    <p className="text-xs text-slate-500 mt-0.5">
                      Total : {timeEntries.reduce((s, e) => s + e.duration_minutes, 0)} min ·{' '}
                      {timeEntries.reduce((s, e) => s + e.amount_euros, 0).toFixed(2)} € HT
                    </p>
                  )}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => startTimer(id, dossier?.title || 'Dossier')}
                    disabled={isTimerRunning}
                    className="flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                  >
                    <Clock size={14} />
                    {isTimerRunning ? 'Chrono actif' : 'Démarrer le chrono'}
                  </button>
                  <button
                    onClick={() => setShowAddTime(true)}
                    className="flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700"
                  >
                    <Plus size={14} /> Ajouter
                  </button>
                </div>
              </div>

              {/* Dossier hourly rate */}
              <div className="flex items-center gap-3 rounded-lg border border-slate-200 bg-slate-50 px-4 py-2">
                <span className="text-sm text-slate-600">Taux horaire du dossier :</span>
                <input
                  type="number"
                  min="0"
                  value={dossierRate ?? ''}
                  onChange={(e) => setDossierRate(e.target.value)}
                  onBlur={(e) => handleSaveDossierRate(e.target.value)}
                  placeholder="Taux avocat par défaut"
                  className="w-28 rounded border border-slate-300 px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
                <span className="text-sm text-slate-500">€/h</span>
              </div>

              {/* Add time entry form */}
              {showAddTime && (
                <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-3">
                  <h4 className="text-sm font-medium text-slate-800">Nouvelle saisie</h4>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="col-span-2">
                      <label className="block text-xs font-medium text-slate-600 mb-1">Description *</label>
                      <input
                        type="text"
                        value={timeForm.description}
                        onChange={(e) => setTimeForm(f => ({ ...f, description: e.target.value }))}
                        placeholder="Ex : Rédaction conclusions"
                        className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Date *</label>
                      <input
                        type="date"
                        value={timeForm.date}
                        onChange={(e) => setTimeForm(f => ({ ...f, date: e.target.value }))}
                        className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Durée (minutes) *</label>
                      <input
                        type="number"
                        min="1"
                        value={timeForm.duration_minutes}
                        onChange={(e) => setTimeForm(f => ({ ...f, duration_minutes: e.target.value }))}
                        className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                  <div className="flex gap-2 justify-end">
                    <button onClick={() => setShowAddTime(false)} className="px-3 py-1.5 text-sm text-slate-600 hover:text-slate-900">
                      Annuler
                    </button>
                    <button
                      onClick={handleSaveTimeEntry}
                      className="rounded-lg bg-blue-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-blue-700"
                    >
                      Enregistrer
                    </button>
                  </div>
                </div>
              )}

              {/* Time entries list */}
              {timeLoading ? (
                <div className="space-y-2">
                  {[1, 2, 3].map(i => (
                    <div key={i} className="h-12 animate-pulse rounded-lg bg-slate-100" />
                  ))}
                </div>
              ) : timeEntries.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <Clock className="mb-3 h-8 w-8 text-slate-300" />
                  <p className="text-sm font-medium text-slate-600">Aucune saisie de temps</p>
                  <p className="mt-1 text-xs text-slate-400">Démarrez le chrono ou ajoutez une saisie manuelle.</p>
                  <button
                    onClick={() => setShowAddTime(true)}
                    className="mt-3 rounded-lg bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700"
                  >
                    Ajouter une saisie
                  </button>
                </div>
              ) : (
                <div className="divide-y divide-slate-100 rounded-xl border border-slate-200 bg-white">
                  {timeEntries.map((entry) => (
                    <div key={entry.id} className="flex items-center gap-3 px-4 py-3">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-800 truncate">{entry.description}</p>
                        <p className="text-xs text-slate-500">{entry.date} · {entry.duration_minutes} min · {entry.rate_euros} €/h</p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-sm font-semibold text-slate-900">{entry.amount_euros.toFixed(2)} €</p>
                        <span className={`inline-block text-xs px-1.5 py-0.5 rounded ${
                          entry.status === 'invoiced'
                            ? 'bg-emerald-100 text-emerald-700'
                            : 'bg-slate-100 text-slate-500'
                        }`}>
                          {entry.status === 'invoiced' ? 'Facturé' : 'Non facturé'}
                        </span>
                      </div>
                      {entry.status === 'draft' && (
                        <button
                          onClick={() => handleDeleteTimeEntry(entry.id)}
                          className="ml-1 text-slate-400 hover:text-red-500"
                          title="Supprimer"
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default DossierDetail;